"use client"

import { CUSTOMERS } from "@/constants/admin-paths"
import { t } from "@/languages/i18n"
import { normalizeText } from "@/lib/external-components"
import {
  addCustomer,
  deleteCustomer,
  fetchCustomerId,
  fetchCustomers,
  updateCustomer,
} from "@/services/customerServices"
import { PaginationMeta } from "@/types/event"
import { CustomerData, CustomerFieldType, CustomerPayload, ReceiveSMS } from "@/types/types"
import { generateDuplicateName } from "@/utils/duplicate-name-helper"
import { App, FormInstance } from "antd"
import { useRouter } from "next/navigation"
import queryString from "query-string"
import { useCallback, useRef, useState } from "react"

export const useCustomers = () => {
  const router = useRouter()
  const { message, modal } = App.useApp()

  const [dataItem, setDataItem] = useState<CustomerData[]>([])
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalItem, setTotalItem] = useState(0)
  const [customerNameOptions, setCustomerNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  // Guards: reqSeq drops stale responses, inFlightRequestKeyRef collapses the
  // React 19 StrictMode double-invoke into one request.
  const reqSeq = useRef(0)
  const inFlightRequestKeyRef = useRef<string | null>(null)
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: 20,
    total: 0,
    pageCount: 0,
  })

  const fetchCustomersData = useCallback(
    async (searchTerms?: { [key: string]: string }) => {
      const params = {
        page: String(pagination.page),
        pageSize: String(pagination.pageSize),
        ...searchTerms,
      }
      const requestKey = JSON.stringify(params)
      if (inFlightRequestKeyRef.current === requestKey) {
        return
      }
      inFlightRequestKeyRef.current = requestKey
      const seq = ++reqSeq.current
      setLoading(true)
      try {
        const allResponse = await fetchCustomers(params)
        if (seq !== reqSeq.current) return // superseded by a newer request
        const response = allResponse && allResponse.data
        const meta = allResponse && allResponse.meta
        setMetaData(meta)

        if (response && response.length > 0) {
          setDataItem(response)
          setTotalItem(meta?.pagination?.total || 0)
          setCustomerNameOptions(
            response.map((customer: CustomerData) => ({
              value: customer.id,
              label: customer.attributes.fullname || customer.attributes.email,
            })),
          )
        } else {
          setDataItem([])
          setTotalItem(0)
          setCustomerNameOptions([])
        }

        // Only page/total/pageCount — pageSize stays owned by viewConfig (RC2).
        if (meta?.pagination) {
          const p = meta.pagination
          setPagination(prev => ({
            ...prev,
            page: p.page ?? prev.page,
            total: p.total ?? 0,
            pageCount: p.pageCount ?? 0,
          }))
        }

        return response
      } catch (error) {
        message.error(t("Tải danh sách khách hàng thất bại. Vui lòng thử lại."))
      } finally {
        if (inFlightRequestKeyRef.current === requestKey) {
          inFlightRequestKeyRef.current = null
        }
        if (seq === reqSeq.current) setLoading(false)
      }
    },
    [message, pagination.page, pagination.pageSize],
  )

  const fetchCustomerByIdAndSetForm = useCallback(
    async (id: number, form?: FormInstance) => {
      try {
        const response = await fetchCustomerId(id)

        if (!response || !response.attributes) {
          message.error(t("Không tìm thấy khách hàng hoặc dữ liệu không hợp lệ"))
          return { customer: null, formData: null, signatureMedia: null }
        }

        const customerData = response.attributes
        let signatureValue: string | null = null
        let signatureMedia: any = null

        if (customerData.signature && typeof customerData.signature === "object" && "data" in customerData.signature) {
          const signatureData = (customerData.signature as any).data
          if (signatureData && signatureData.id) {
            signatureValue = String(signatureData.id)
            signatureMedia = signatureData
          }
        } else if (customerData.signature) {
          signatureValue = String(customerData.signature)
        }

        const formData: CustomerFieldType = {
          ico: customerData.ico || "",
          dic: customerData.dic || "",
          businessName: customerData.businessName || "",
          businessRepresentative: customerData.businessRepresentative || "",
          phone: customerData.phone || "",
          dphRegistered: customerData.dphRegistered !== undefined ? customerData.dphRegistered : false,
          email: customerData.email || "",
          address: customerData.address || "",
          city: customerData.city || "",
          countryOrState: customerData.countryOrState || "",
          receiveAtSore: customerData.receiveAtSore || "",
          haveVAT: customerData.haveVAT !== undefined ? customerData.haveVAT : false,
          receiveSMS: customerData.receiveSMS || ReceiveSMS.NO,
          signature: signatureValue || undefined,
          idNumber: customerData.idNumber || "",
          occupation: customerData.occupation || "",
          fullname: customerData.fullname || "",
          registrationCode: customerData.registrationCode || "",
          birthNumber: customerData.birthNumber || "",
          isSentEmail: customerData.isSentEmail !== undefined ? customerData.isSentEmail : false,
          isResolved: customerData.isResolved !== undefined ? customerData.isResolved : false,
        }

        if (form) {
          form.setFieldsValue(formData)
        }

        return { customer: response, formData, signatureMedia }
      } catch (error) {
        console.error("Error fetching customer:", error)
        message.error(t("Lỗi khi tải thông tin khách hàng"))
        return { customer: null, formData: null, signatureMedia: null }
      }
    },
    [message],
  )

  const handleDelete = useCallback(
    async (id: number, filterValues?: any, sortPaginationValues?: any) => {
      modal.confirm({
        title: t("Xác nhận xóa"),
        content: t("Bạn có chắc chắn muốn xóa khách hàng này không?"),
        okText: t("Xóa"),
        cancelText: t("Hủy"),
        onOk: async () => {
          try {
            setLoading(true)
            await deleteCustomer(id)
            const currentParams = {
              ...filterValues,
              ...sortPaginationValues,
            }
            fetchCustomersData(currentParams)
            message.success(t("Xóa khách hàng thành công!"))
          } catch {
            message.error(t("Lỗi khi xử lý xóa"))
          }
        },
      })
    },
    [message, fetchCustomersData],
  )

  const handleBulkDelete = useCallback(
    async (filterValues?: any, sortPaginationValues?: any) => {
      try {
        setLoading(true)
        const ids = selectedRowKeys.map(id => Number(id))
        await Promise.all(ids.map(id => deleteCustomer(id)))
        setSelectedRowKeys([])
        const currentParams = {
          ...filterValues,
          ...sortPaginationValues,
        }
        fetchCustomersData(currentParams)
        message.success(t("Xóa khách hàng thành công!"))
      } catch {
        message.error(t("Lỗi khi xử lý xóa"))
      } finally {
        setLoading(false)
      }
    },
    [selectedRowKeys, message, fetchCustomersData],
  )

  const handleApplyBulkAction = useCallback(
    (filterValues?: any, sortPaginationValues?: any) => {
      if (bulkAction === "delete" && selectedRowKeys.length > 0) {
        modal.confirm({
          title: t("Xác nhận xóa"),
          content: t("Bạn có chắc chắn muốn xóa những khách hàng đã chọn không?"),
          okText: t("Xóa"),
          cancelText: t("Hủy"),
          onOk: () => handleBulkDelete(filterValues, sortPaginationValues),
        })
      } else if (selectedRowKeys.length === 0) {
        message.warning(t("Vui lòng chọn khách hàng để thực hiện thao tác."))
      } else {
        message.warning(t("Vui lòng chọn một thao tác hàng loạt hợp lệ."))
      }
    },
    [bulkAction, selectedRowKeys, message, handleBulkDelete],
  )

  const handleEdit = useCallback(
    (id: number) => {
      router.push(`/admin${CUSTOMERS.EDIT.PATH}?id=${id}`)
    },
    [router],
  )

  const handleRefresh = useCallback(
    (filterRef?: any) => {
      if (filterRef?.current) {
        filterRef.current.filterFunction()
      }
      setNeedRefresh(!needRefresh)
    },
    [needRefresh],
  )

  const handleSearch = useCallback(
    (filters: object, pathname: string) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error(t("Giá trị không xác định"))
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${pathname}?${searchParams}` : pathname
      router.push(`${newUrl}`)
    },
    [router, message],
  )

  const handleUpsertCustomer = useCallback(
    async (values: CustomerFieldType, customerId?: number) => {
      const dataPayload: CustomerPayload = {
        data: {
          ico: values.ico || "",
          dic: values.dic || "",
          businessName: values.businessName || "",
          businessRepresentative: values.businessRepresentative || "",
          phone: values.phone,
          dphRegistered: values.dphRegistered !== undefined ? values.dphRegistered : false,
          email: values.email,
          address: values.address || "",
          city: values.city || "",
          countryOrState: values.countryOrState || "",
          receiveAtSore: values.receiveAtSore || "",
          haveVAT: values.haveVAT !== undefined ? values.haveVAT : false,
          receiveSMS: (values.receiveSMS as ReceiveSMS) || ReceiveSMS.NO,
          ...(values.signature && typeof values.signature === "string" && values.signature.trim() !== ""
            ? { signature: values.signature }
            : {}),
          idNumber: values.idNumber || "",
          occupation: values.occupation || "",
          fullname: values.fullname || "",
          registrationCode: values.registrationCode || "",
          birthNumber: values.birthNumber || "",
          isSentEmail: values.isSentEmail !== undefined ? values.isSentEmail : false,
          isResolved: values.isResolved !== undefined ? values.isResolved : false,
        },
      }

      try {
        if (customerId) {
          await updateCustomer(customerId, dataPayload)
          message.success(t("Cập nhật khách hàng thành công!"))
          return { success: true, id: customerId, isEdit: true }
        } else {
          const response = await addCustomer(dataPayload)
          return { success: true, id: response.data.id, isEdit: false }
        }
      } catch (error) {
        if (customerId) {
          message.error(t("Cập nhật khách hàng thất bại. Vui lòng thử lại."))
        } else {
          message.error(t("Thêm khách hàng thất bại. Vui lòng thử lại."))
        }
        return { success: false, id: null, isEdit: !!customerId }
      }
    },
    [message],
  )

  const handleDuplicate = useCallback(
    async (customer: CustomerData, filterValues?: any, sortPaginationValues?: any) => {
      try {
        setLoading(true)

        const existingBusinessNames = dataItem
          .map(item => item.attributes.businessName)
          .filter((name): name is string => !!name)

        const duplicatedData: CustomerFieldType = {
          ...customer.attributes,
          businessName: generateDuplicateName(customer.attributes.businessName || "", existingBusinessNames),
        }

        await handleUpsertCustomer(duplicatedData)

        const currentParams = {
          ...filterValues,
          ...sortPaginationValues,
        }
        await fetchCustomersData(currentParams)
        message.success(t("Nhân bản khách hàng thành công!"))
      } catch (error) {
        message.error(t("Nhân bản khách hàng thất bại. Vui lòng thử lại."))
      } finally {
        setLoading(false)
      }
    },
    [message, fetchCustomersData, handleUpsertCustomer, dataItem],
  )

  return {
    dataItem,
    loading,
    metaData,
    totalItem,
    customerNameOptions,
    selectedRowKeys,
    bulkAction,
    needRefresh,
    pagination,

    actions: {
      fetchCustomersData,
      fetchCustomerByIdAndSetForm,
      handleDelete,
      handleBulkDelete,
      handleApplyBulkAction,
      handleEdit,
      handleDuplicate,
      handleRefresh,
      handleSearch,
      handleUpsertCustomer,
      setSelectedRowKeys,
      setBulkAction,
      setPagination,
    },

    setters: {
      setDataItem,
      setLoading,
      setMetaData,
      setTotalItem,
      setCustomerNameOptions,
      setSelectedRowKeys,
      setBulkAction,
      setNeedRefresh,
      setPagination,
    },
  }
}
